import Swiper from 'react-id-swiper';
import { useState, useEffect } from 'react';
import { StyledGallery, StyledContent } from '@/components/styles';
import { Modal, Picture } from '@/components';
import Icon from '@/assets/icons';
import vars from '@/assets/styles/vars';

export default function Gallery({ images, initialInd = 0, onChange, inModal = false, slider = true }) {
  const { main: mainImages = [], modal: modalImages = [], modal_thumb: modalThumbImages = [] } = images;
  const [thumbnailSwiper, getThumbnailSwiper] = useState(null);
  const [gallerySwiper, getGallerySwiper] = useState(null);
  const [activeThumb, setActiveThumb] = useState(0);
  const [navDisabled, setNavDisabled] = useState({ prev: true, next: false });
  // locked state to hide navigation buttons when there is nothing to slide
  const [thumbsLocked, setThumbsLocked] = useState(true);

  useEffect(() => {
    if (gallerySwiper !== null && gallerySwiper.controller) {
      setActiveThumb(initialInd);
      gallerySwiper.on('slideChange', () => {
        setActiveThumb(gallerySwiper.activeIndex);
        thumbnailSwiper && thumbnailSwiper.slideTo(gallerySwiper.activeIndex);
        onChange && onChange(gallerySwiper.activeIndex);
      });
    }
  }, [gallerySwiper]);

  useEffect(() => {
    if (thumbnailSwiper !== null && thumbnailSwiper.controller) {
      setThumbsLocked(thumbnailSwiper.isLocked);
      setNavDisabled({ prev: thumbnailSwiper.isBeginning, next: thumbnailSwiper.isEnd });
      thumbnailSwiper.on('resize', () => {
        setThumbsLocked(thumbnailSwiper.isLocked);
        setNavDisabled({ prev: thumbnailSwiper.isBeginning, next: thumbnailSwiper.isEnd });
      });
      thumbnailSwiper.on('slideChange', () => {
        setNavDisabled({ prev: thumbnailSwiper.isBeginning, next: thumbnailSwiper.isEnd });
      });
      thumbnailSwiper.on('reachEnd', () => {
        setNavDisabled({ prev: thumbnailSwiper.isBeginning, next: thumbnailSwiper.isEnd });
      });
      thumbnailSwiper.on('reachBeginning', () => {
        setNavDisabled({ prev: thumbnailSwiper.isBeginning, next: thumbnailSwiper.isEnd });
      });
    }
  }, [thumbnailSwiper]);

  const thumbnailSwiperParams = {
    preloadImages: false,
    getSwiper: getThumbnailSwiper,
    slidesPerView: 3,
    spaceBetween: 8,
    watchOverflow: true,
    watchSlidesVisibility: true,
    resistanceRatio: 0.5,
    freeMode: true,
    initialSlide: initialInd,
    breakpoints: {
      [vars.breakpoints.lMobile + 1]: {
        slidesPerView: 6,
        spaceBetween: 20,
      },
    },
  };

  const thumbnailGoNext = () => {
    if (thumbnailSwiper !== null) {
      thumbnailSwiper.slideNext();
    }
  };
  const thumbnailGoPrev = () => {
    if (thumbnailSwiper !== null) {
      thumbnailSwiper.slidePrev();
    }
  };

  const gallerySwiperParams = {
    preloadImages: false,
    getSwiper: getGallerySwiper,
    containerClass: 'swiper-container swiper-container--light',
    effect: 'fade',
    preventInteractionOnTransition: true,
    resistanceRatio: 0,
    slidesPerView: 1,
    initialSlide: initialInd,
    keyboard: true,
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
    watchOverflow: true,
    renderPrevButton: () => (
      <div className="swiper-button-prev">
        <Icon name="arrow-left" width="16" height="10" />
      </div>
    ),
    renderNextButton: () => (
      <div className="swiper-button-next">
        <Icon name="arrow-right" width="16" height="10" />
      </div>
    ),
    pagination: {
      el: '.swiper-pagination',
      type: 'progressbar',
    },
  };

  return (
    <StyledGallery modal={inModal}>
      {slider ? (
        <StyledGallery.Inner>
          <StyledGallery.Main>
            <Swiper {...gallerySwiperParams}>
              {mainImages.map((image, ind) => {
                const imageAlt = image.preview_alt || '';
                return (
                  <StyledGallery.Img key={ind}>
                    <Picture {...image} alt={imageAlt} isLazy={true} />

                    {!inModal && (
                      <Modal render={StyledGallery.ModalTrigger} view="gallery">
                        <Gallery
                          inModal
                          images={{ main: modalImages, modal_thumb: modalThumbImages }}
                          initialInd={activeThumb}
                          onChange={(ind) => {
                            setActiveThumb(ind);
                            gallerySwiper.slideTo(ind);
                            thumbnailSwiper && thumbnailSwiper.slideTo(gallerySwiper.activeIndex);
                          }}
                        />
                      </Modal>
                    )}
                  </StyledGallery.Img>
                );
              })}
            </Swiper>
          </StyledGallery.Main>

          {inModal && !!modalThumbImages?.length && (
            <StyledGallery.Thumbs locked={thumbsLocked}>
              <Swiper {...thumbnailSwiperParams}>
                {modalThumbImages.map((image, ind) => {
                  const imageAlt = image.preview_alt || '';
                  return (
                    <StyledGallery.Thumb
                      key={ind}
                      onClick={() => {
                        gallerySwiper.slideTo(ind);
                        thumbnailSwiper.slideTo(ind);
                        setActiveThumb(gallerySwiper.activeIndex);
                      }}
                      active={activeThumb === ind}
                    >
                      <Picture {...image} alt={imageAlt} isLazy={true} />
                    </StyledGallery.Thumb>
                  );
                })}
              </Swiper>
              <button
                className={navDisabled.prev ? 'swiper-button-prev swiper-button-disabled' : 'swiper-button-prev'}
                onClick={thumbnailGoPrev}
              >
                <Icon name="arrow-left" width="16" height="10" />
              </button>
              <button
                className={navDisabled.next ? 'swiper-button-next swiper-button-disabled' : 'swiper-button-next'}
                onClick={thumbnailGoNext}
              >
                <Icon name="arrow-right" width="16" height="10" />
              </button>
            </StyledGallery.Thumbs>
          )}
        </StyledGallery.Inner>
      ) : (
        <StyledContent.Cards items={2} type="images">
          {mainImages.map((image, ind) => {
            const imageAlt = image.preview_alt || '';
            return (
              <div
                key={ind}
                style={{ position: 'relative' }}
                onClick={() => {
                  console.log(ind);
                  setActiveThumb(ind);
                }}
              >
                <Picture {...image} alt={imageAlt} isLazy={true}/>
                {!inModal && (
                  <Modal render={StyledGallery.ModalTrigger} view="gallery">
                    <Gallery
                      inModal
                      images={{ main: modalImages }}
                      initialInd={activeThumb}
                      onChange={(ind) => {
                        setActiveThumb(ind);
                        gallerySwiper.slideTo(ind);
                        thumbnailSwiper && thumbnailSwiper.slideTo(gallerySwiper.activeIndex);
                      }}
                    />
                  </Modal>
                )}
              </div>
            );
          })}
        </StyledContent.Cards>
      )}
    </StyledGallery>
  );
}
